import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import React, { useState } from 'react';
import { useAppSelector } from '../../app/hooks';
import OrderReview from '../../features/order-review/OrderReview';
import { selectIsAICartTheOrder } from '../../redux/features/agentMode/agentMode.selector';
import { selectCartItemsQuantity } from '../../selectors/cart';
import { selectIsCarPresent } from '../../selectors/message';
import Colors from '../../utils/color';
import { HITL3_PADDING } from '../../utils/hitl3Spacing';
import ConsoleCard from './ConsoleCard';

const useStyles = makeStyles<Theme>(() => ({
  count: {
    color: Colors.silverChalice,
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  // Scrolls the cart line items internally so the scrollbar stays inside the
  // card, not on the outer resizable panel. 8px left/right inset around
  // OrderReview; no top/bottom inset.
  itemsScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: `0 ${HITL3_PADDING}`,
  },
}));

interface AgentCartCardProps {
  // Controlled collapse so the parent drives the resizable panel; falls back to
  // internal state (default open) when used standalone.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * AGENT CART card (right pane, top). The working cart's line items + modifiers
 * (OrderReview) under an "Agent Cart" heading with a live item count. The totals
 * and Cancel / Finish actions are NOT here — they are pinned in the RightSidebar
 * footer so they stay visible however the cards are resized or collapsed.
 */
const AgentCartCard = ({
  open: openProp,
  onOpenChange,
}: AgentCartCardProps = {}) => {
  const classes = useStyles();
  const [openState, setOpenState] = useState(true);
  const open = openProp ?? openState;
  const toggleOpen = () => {
    const next = !open;
    if (openProp === undefined) setOpenState(next);
    onOpenChange?.(next);
  };

  const itemQuantities = useAppSelector(selectCartItemsQuantity);
  const isCarPresent = useAppSelector(selectIsCarPresent);
  // HITL-1614: this column is "Agent Cart" whenever it IS the order — flag off,
  // or `Intercept`, where the agent has taken over and the AI is silent. In
  // `Supervise` and `Shadow` it is the AI's work and the agent is watching it,
  // so it says so. With the flag off the selector is always true and the title
  // is the string it has always been.
  const isAICartTheOrder = useAppSelector(selectIsAICartTheOrder);

  // selectCartItemsQuantity is a per-item map; total = sum of the quantities.
  const itemCount = React.useMemo(
    () =>
      Object.values(itemQuantities ?? {}).reduce(
        (sum, qty) => sum + (qty || 0),
        0
      ),
    [itemQuantities]
  );

  return (
    <ConsoleCard
      icon={<ShoppingCartOutlinedIcon />}
      title={isAICartTheOrder ? 'Agent Cart' : 'AI Cart'}
      open={open}
      onToggle={toggleOpen}
      testId="agent-cart-card"
      // OrderReview is heavy and its effects must keep syncing the order even
      // while collapsed — keep it mounted (hidden) so toggling is instant.
      keepBodyMounted
      headerRight={
        isCarPresent ? (
          <span className={classes.count}>
            {itemCount} {itemCount === 1 ? 'item' : 'items'}
          </span>
        ) : undefined
      }
    >
      <div id="agent-cart" className={classes.itemsScroll}>
        <OrderReview />
      </div>
    </ConsoleCard>
  );
};

export default AgentCartCard;
